/**
 * /privacy — NDORFlow Privacy Policy (production, GDPR / UK-DPA compliant).
 *
 * RC 1.35.F1 P0-2 — replaces the 20-line `PrivacyPage.js` stub.
 *
 * This file is imported by:
 *   - App.js → route `/privacy` (public, standalone legal page)
 *   - pages/PrivacyPage.js → AccountPage > Privacy tab (embedded=true)
 * Both render identical content; only the chrome differs.
 */
import LegalPageShell from './LegalPageShell';

export default function PrivacyPolicyPage({ embedded = false } = {}) {
  return (
    <LegalPageShell
      title="Privacy Policy"
      updatedAt="28 April 2026"
      activePath="/privacy"
      embedded={embedded}
      testid="privacy-page"
    >
      <p>
        The NDORFlow software platform (<strong>"NDORFlow"</strong>) is owned,
        developed and licensed by <strong>NDOR Labs Limited</strong> (the
        "<strong>Software Owner</strong>"). <strong>IAA Energy Resources
        Limited</strong> (Company No. 11583381), a company registered in
        England and Wales, is the Authorised Commercial Operator for NDORFlow
        (the "<strong>Commercial Operator</strong>") and is responsible for
        marketing, sales, subscriptions, billing, payment collection,
        customer onboarding and customer support. References to{' '}
        <strong>"we"</strong>, <strong>"us"</strong> or{' '}
        <strong>"our"</strong> in this Policy refer to the two companies
        acting in their respective capacities set out above and in
        Section&nbsp;1 below. This Privacy Policy explains what personal
        data we collect when you use NDORFlow, how we use and share it,
        how long we keep it, and the rights you have over it. It applies
        to all visitors and registered users of the Service.
      </p>

      {/*
        Internal note (do not render): the controller / processor
        allocation between NDOR Labs Limited and IAA Energy Resources
        Limited described in Section 1 reflects the doctrine defined in
        /app/docs/governance/CORPORATE_RELATIONSHIP_DOCTRINE.md §8
        (Privacy Policy Guidance). External legal-counsel review remains
        a separately tracked follow-up; wording may be refined in a
        future update. Do not add a public "Founder review pending"
        notice — the Founder has approved the current wording.
      */}

      <h2>1. Who is responsible for your personal data?</h2>
      <p>
        NDORFlow is operated as a partnership between two companies. Each handles
        a distinct part of your relationship with the Service, and each is
        responsible for its own processing activities:
      </p>
      <ul>
        <li>
          <strong>NDOR Labs Limited</strong> &mdash; the Software Owner &mdash;
          is responsible for the operation, maintenance, security and
          development of the NDORFlow software platform and its technical
          infrastructure (for example: how the application is built, hosted,
          encrypted, monitored and updated).
        </li>
        <li>
          <strong>IAA Energy Resources Limited</strong> &mdash; the Authorised
          Commercial Operator &mdash; is responsible for your commercial
          relationship with NDORFlow (for example: sign-up, subscriptions,
          billing, payment collection, invoices, customer accounts, customer
          communications and commercial support).
        </li>
      </ul>
      <p>
        Where UK / EU data-protection law requires a designated data controller,
        IAA Energy Resources Limited acts as the controller of your personal
        data for the commercial-operation activities listed above, and NDOR
        Labs Limited acts as the party responsible for platform-operation
        activities on our behalf. For any privacy question or to exercise
        your rights, contact us via a single address:{' '}
        <a href="mailto:support@ndor.app">support@ndor.app</a>.
      </p>

      <h2>2. What data we collect</h2>
      <h3>2.1 Account data</h3>
      <ul>
        <li>Email address, display name, password hash, workspace membership.</li>
        <li>Plan, Credits balance, billing history metadata.</li>
        <li>IP address, device, browser, and log data captured automatically.</li>
      </ul>

      <h3>2.2 Uploaded CVs and documents</h3>
      <ul>
        <li>CV files (PDF, DOCX) you upload.</li>
        <li>Parsed structured representations of those files (sections, experience entries, skills).</li>
        <li>Cover letters, application packs, and notes you create inside the Service.</li>
      </ul>

      <h3>2.3 Job application materials</h3>
      <ul>
        <li>Jobs you mark as recommended, applied, or ignored.</li>
        <li>Fit scores, NDOR analyses, and interview-preparation outputs generated for you.</li>
        <li>Exported bundles (PDF, Word) you have produced.</li>
      </ul>

      <h3>2.4 Billing data</h3>
      <ul>
        <li>We do <strong>not</strong> store your full card or bank details. Payments are processed by Stripe.</li>
        <li>We store Stripe customer IDs, transaction metadata, subscription status, and receipts linked to your account.</li>
      </ul>

      <h3>2.5 Cookies &amp; analytics</h3>
      <p>
        We use strictly necessary cookies for authentication and session management. We may use
        first-party analytics to measure feature usage and product performance. Where required by
        local law (UK/EU), a consent banner is presented for non-essential analytics cookies.
      </p>

      <h2>3. How we use your data</h2>
      <ul>
        <li><strong>To provide the Service</strong> — parse your CVs, generate tailored outputs, score fit, produce packs, deliver exports.</li>
        <li><strong>To bill you</strong> — process subscriptions and top-ups via Stripe, issue receipts, enforce Credits.</li>
        <li><strong>To secure the Service</strong> — detect abuse, enforce rate-limits, investigate security incidents.</li>
        <li><strong>To improve the Service</strong> — aggregate and anonymised analytics; never to train third-party public AI models with your Content.</li>
        <li><strong>To communicate with you</strong> — transactional email (receipts, security notices, material product changes). Marketing email only with explicit opt-in.</li>
        <li><strong>To comply with law</strong> — respond to lawful requests, meet tax and accounting obligations.</li>
      </ul>

      <h2>4. Legal bases (UK GDPR / EU GDPR)</h2>
      <ul>
        <li><strong>Contract</strong> — to provide the Service you signed up for.</li>
        <li><strong>Legitimate interests</strong> — to secure, debug, and improve the Service; balanced against your rights.</li>
        <li><strong>Consent</strong> — for non-essential analytics cookies and marketing email.</li>
        <li><strong>Legal obligation</strong> — tax, accounting, responding to lawful authority requests.</li>
      </ul>

      <h2>5. Third-party processors</h2>
      <p>We rely on the following processors to deliver the Service. Each is bound by a Data Processing Agreement.</p>
      <ul>
        <li><strong>Supabase</strong> — authentication, Postgres database, object storage. Primary region: EU (Frankfurt).</li>
        <li><strong>Stripe</strong> — payment processing, subscription management, Stripe Customer Portal.</li>
        <li><strong>OpenAI, Anthropic, Google Gemini</strong> — large-language-model inference for CV tailoring, cover-letter generation, fit scoring, and interview-preparation outputs. We send the minimum context required for each inference; we do not allow the provider to use your data to train their models (enterprise-grade settings are enabled where available).</li>
        <li><strong>Gmail OAuth (Google)</strong> — optional; only enabled if you connect your Gmail account to send application emails. Scope is limited to drafting and sending from your own account.</li>
        <li><strong>Email delivery (Resend / SendGrid)</strong> — transactional email.</li>
        <li><strong>Hosting &amp; CDN</strong> — application hosting and content-delivery network.</li>
        <li><strong>PostHog</strong> — first-party product analytics. We capture page views and discrete product events (e.g. paid action attempted, paid action completed) to understand how the Service is used and to debug failures. Session-recording is currently disabled. We do not use PostHog for advertising or cross-site tracking. PostHog acts as a processor under our instructions; see the{' '}
          <a href="https://posthog.com/privacy" rel="noreferrer" target="_blank">PostHog Privacy Policy</a> and{' '}
          <a href="https://posthog.com/dpa" rel="noreferrer" target="_blank">Data Processing Agreement</a>.
        </li>
      </ul>

      <h2>6. International transfers</h2>
      <p>
        Some processors are located outside the UK / EEA. When we transfer personal data
        internationally we rely on UK IDTA or EU Standard Contractual Clauses, supplemented by
        technical safeguards (encryption in transit and at rest) to ensure an adequate level of
        protection.
      </p>

      <h2>7. Security</h2>
      <ul>
        <li>TLS 1.2+ for all traffic.</li>
        <li>Encryption at rest for the database and object storage.</li>
        <li>Least-privilege access controls, MFA on administrative accounts.</li>
        <li>Row-level-security policies gating all user-owned tables.</li>
        <li>Regular backups; Supabase operates point-in-time recovery.</li>
        <li>Independent security reviews on major releases.</li>
      </ul>

      <h2>8. Retention</h2>
      <ul>
        <li><strong>Active accounts.</strong> We retain your account data and Content for as long as your account is active.</li>
        <li><strong>Closed accounts.</strong> On account closure, we delete your Content within 90 days, except where we must retain billing records for legal / tax reasons (typically 6 years in the UK).</li>
        <li><strong>Anonymised metrics.</strong> We may retain aggregated, non-identifying usage statistics indefinitely.</li>
        <li><strong>Backups.</strong> Deleted Content may persist in encrypted backups for up to 35 days before rotation.</li>
      </ul>

      <h2>9. Your rights</h2>
      <p>Subject to local law, you have the right to:</p>
      <ul>
        <li>Access the personal data we hold about you.</li>
        <li>Rectify inaccurate data.</li>
        <li>Erase data ("right to be forgotten") — subject to retention obligations above.</li>
        <li>Port your data to another service in a machine-readable format.</li>
        <li>Restrict or object to processing.</li>
        <li>Withdraw consent (for consent-based processing).</li>
        <li>Lodge a complaint with a supervisory authority (in the UK, the Information Commissioner's Office — <a href="https://ico.org.uk/" rel="noreferrer" target="_blank">ico.org.uk</a>).</li>
      </ul>
      <p>
        To exercise any of these rights, email{' '}
        <a href="mailto:support@ndor.app">support@ndor.app</a>. We respond within 30 days.
      </p>

      <h2>10. Children</h2>
      <p>
        The Service is not intended for and may not be used by anyone under 16. We do not knowingly
        collect data from children. If you believe a child has created an account, contact us and we
        will delete the account.
      </p>

      <h2>11. Automated decision-making</h2>
      <p>
        NDORFlow does not make automated decisions that produce legal or similarly significant effects
        about you. AI-generated CVs, cover letters, and fit scores are decision-support outputs — you
        review and edit them before acting on them.
      </p>

      <h2>12. Changes to this Policy</h2>
      <p>
        We may update this Privacy Policy from time to time. Material changes will be notified by email
        and/or in-product banner at least 14 days before they take effect. The "Last updated" date at
        the top of this page reflects the current version.
      </p>

      <h2>13. Contact</h2>
      <p>
        For privacy questions or to exercise your rights, email{' '}
        <a href="mailto:support@ndor.app">support@ndor.app</a>. For billing privacy questions,
        email <a href="mailto:support@ndor.app">support@ndor.app</a>. For our Terms of Service
        and Refund Policy, see <a href="/terms">/terms</a> and <a href="/refund">/refund</a>.
      </p>
    </LegalPageShell>
  );
}
